import type {
  FoundationAssemblyEntityTable,
  FoundationAssemblyId,
} from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import { assertUnreachable } from "common/utils/assertUnreachable";
import {
  defaultStripFootingId,
  defaultTurndownFootingId,
  defaultCircularIsolatedFootingId,
  defaultRectangularIsolatedFootingId,
  defaultCIPStemWallId,
  defaultCMUStemWallId,
  defaultCircularCIPPierId,
  defaultRectangularCIPPierId,
} from "./defaultFoundationComponents";
import { foundationAssemblyNewEntity } from "./foundationAssemblyEntity";

export const defaultMonolithicAssemblyId: FoundationAssemblyId = "defaultMonolithicAssembly";
export const defaultCIPStemWallAssemblyId: FoundationAssemblyId = "defaultCIPStemWallAssembly";
export const defaultCMUStemWallAssemblyId: FoundationAssemblyId = "defaultCMUStemWallAssembly";
export const defaultIsolatedCircularAssemblyId: FoundationAssemblyId = "defaultIsolatedCircularAssembly";
export const defaultIsolatedRectangularAssemblyId: FoundationAssemblyId = "defaultIsolatedRectangularAssembly";

const defaultFoundationAssembliesInternal = {
  [defaultMonolithicAssemblyId]: foundationAssemblyNewEntity(
    "Monolithic Foundation",
    FoundationSubSystemType.MONOLITHIC,
    defaultTurndownFootingId,
    undefined,
    undefined,
    defaultMonolithicAssemblyId,
  ),
  [defaultCIPStemWallAssemblyId]: foundationAssemblyNewEntity(
    "CIP Stem Wall Foundation",
    FoundationSubSystemType.STEM_WALL_CIP,
    defaultStripFootingId,
    defaultCIPStemWallId,
    undefined,
    defaultCIPStemWallAssemblyId,
  ),
  [defaultCMUStemWallAssemblyId]: foundationAssemblyNewEntity(
    "CMU Stem Wall Foundation",
    FoundationSubSystemType.STEM_WALL_CMU,
    defaultStripFootingId,
    defaultCMUStemWallId,
    undefined,
    defaultCMUStemWallAssemblyId,
  ),
  [defaultIsolatedCircularAssemblyId]: foundationAssemblyNewEntity(
    "Isolated Circular Pier Foundation",
    FoundationSubSystemType.ISOLATED,
    defaultCircularIsolatedFootingId,
    undefined,
    defaultCircularCIPPierId,
    defaultIsolatedCircularAssemblyId,
  ),
  [defaultIsolatedRectangularAssemblyId]: foundationAssemblyNewEntity(
    "Isolated Rectangular Pier Foundation",
    FoundationSubSystemType.ISOLATED,
    defaultRectangularIsolatedFootingId,
    undefined,
    defaultRectangularCIPPierId,
    defaultIsolatedRectangularAssemblyId,
  ),
} as const;

export const builtInFoundationAssemblies: FoundationAssemblyEntityTable = defaultFoundationAssembliesInternal;

export const builtInFoundationAssembliesGroupId = "builtInFoundationAssembliesGroup";

export function defaultFoundationAssemblyId(assemblySystem: FoundationSubSystemType): FoundationAssemblyId {
  switch (assemblySystem) {
    case FoundationSubSystemType.MONOLITHIC: {
      return defaultMonolithicAssemblyId;
    }
    case FoundationSubSystemType.STEM_WALL_CIP: {
      return defaultCIPStemWallAssemblyId;
    }
    case FoundationSubSystemType.STEM_WALL_CMU: {
      return defaultCMUStemWallAssemblyId;
    }
    case FoundationSubSystemType.ISOLATED: {
      return defaultIsolatedCircularAssemblyId;
    }
    default: {
      assertUnreachable(assemblySystem);
    }
  }
}

export function isBuiltInFoundationAssembly(id: FoundationAssemblyId): boolean {
  return Object.values(builtInFoundationAssemblies).some((assembly) => assembly.id === id);
}
